import http from "@/axios/http-common";
import ErrorAlert from "@/utility/ErrorAlert";

export type Rl1Status = "ideal" | "sub_ideal" | "jauh_ideal" | null;

export interface Rl1Indikator {
  kode: "BOR" | "LOS" | "TOI" | "BTO" | "GDR" | "NDR";
  nama: string;
  nilai: number | null;
  satuan: string;
  ideal: string;
  status: Rl1Status;
}

export interface Rl1BedCapacityRincian {
  unit_kode: string;
  unit: string;
  kelas: string;
  kapasitas: number;
}

export interface Rl1Matriks {
  unit_kode: string;
  unit_nama: string;
  kelas: Record<string, number>;
  total: number;
}

export interface Rl1Agregat {
  hp: number;
  ld: number;
  pk: number;
  pk_hidup: number;
  mati_total: number;
  mati_ge48: number;
  tt_x_t: number;
}

export interface Rl1PerUnit {
  unit_kode: string;
  unit_nama: string;
  tt: number;
  hp: number;
  ld: number;
  pk: number;
  bor: number | null;
  los: number | null;
  toi: number | null;
  bto: number | null;
}

export interface Rl1RanapResponse {
  periode: { start: string; end: string; days: number; label: string };
  kapasitas_tt: {
    total: number;
    rincian: Rl1BedCapacityRincian[];
    matriks: Rl1Matriks[];
  };
  agregat: Rl1Agregat;
  indikator: Rl1Indikator[];
  per_unit: Rl1PerUnit[];
  params: Record<string, unknown>;
}

export interface Rl1RanapFilter {
  periode_start: string;
  periode_end: string;
  unit?: string[];
  kelas?: string[];
  kelas_kl?: string[];
  cara_bayar?: string[];
}

function toQuery(filter: Rl1RanapFilter): string {
  const sp = new URLSearchParams();
  sp.set("periode_start", filter.periode_start);
  sp.set("periode_end", filter.periode_end);
  (filter.unit ?? []).forEach(u => {
    sp.append("unit[]", u);
  });
  (filter.kelas ?? []).forEach(k => {
    sp.append("kelas[]", k);
  });
  (filter.kelas_kl ?? []).forEach(k => {
    sp.append("kelas_kl[]", k);
  });
  (filter.cara_bayar ?? []).forEach(a => {
    sp.append("cara_bayar[]", a);
  });
  return sp.toString();
}

async function preview(filter: Rl1RanapFilter): Promise<Rl1RanapResponse> {
  const res = await http
    .getDataParam(`/laporan/rl1-ranap`, {
      params: {
        periode_start: filter.periode_start,
        periode_end: filter.periode_end,
        unit: filter.unit,
        kelas: filter.kelas,
        kelas_kl: filter.kelas_kl,
        cara_bayar: filter.cara_bayar
      }
    })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

function openPdf(filter: Rl1RanapFilter): void {
  window.open(`${http.getAPI()}/laporan/rl1-ranap/pdf?${toQuery(filter)}`);
}

export type Rp1Report =
  | "rekap-harian"
  | "jumlah-pasien"
  | "data-kematian"
  | "jenis-bayar"
  | "data-penderita"
  | "data-penderita-meninggal"
  | "data-bor";

export type Rp1PenderitaJenis = "bangsal" | "judul" | "sub" | "tambahan";

export interface Rp1PenderitaWard {
  no: string;
  label: string;
  units: string[];
  jenis: Rp1PenderitaJenis;
  L: number | null;
  P: number | null;
  jumlah: number | null;
}

export interface Rp1PenderitaPayer {
  key: string | null;
  no: string;
  label: string;
  jenis: Rp1PenderitaJenis;
  jumlah: number | null;
}

export type Rp1KelasKey = "iso" | "vip" | "k1" | "k2" | "k3" | "tk";

export type Rp1BayarKey =
  | "umum"
  | "askes_pns"
  | "bpjs_pbi"
  | "jamkesos"
  | "jampersal"
  | "pihak_ketiga"
  | "bpjs"
  | "jamkesda"
  | "taspen"
  | "inhealth"
  | "asabri"
  | "kemenkes"
  | "bpjs_naker";

export type Rp1RinciAngka<K extends string> = Record<K, number | null>;

export interface Rp1PenderitaRinciBaris<K extends string> {
  awal: Rp1RinciAngka<K> & { jumlah: number | null };
  masuk: Rp1RinciAngka<K> & { jumlah: number | null };
  dirawat: Rp1RinciAngka<K>;
  jumlah: number | null;
}

export interface Rp1PenderitaRinciRow<K extends string> extends Rp1PenderitaRinciBaris<K> {
  no: string;
  label: string;
  units: string[];
  jenis: Rp1PenderitaJenis;
}

export interface Rp1PenderitaRinci<K extends string> {
  kolom: { key: K; label: string; nama?: string }[];
  rows: Rp1PenderitaRinciRow<K>[];
  total: Rp1PenderitaRinciBaris<K>;
}

export type Rp1PenderitaKelas = Rp1PenderitaRinci<Rp1KelasKey>;

export type Rp1PenderitaBayar = Rp1PenderitaRinci<Rp1BayarKey>;

export interface Rp1DataPenderita {
  wards: Rp1PenderitaWard[];
  ward_total: { L: number; P: number; jumlah: number };
  payers: Rp1PenderitaPayer[];
  payer_total: number;
  pihak_ketiga_rincian: { kode: string; nama: string; jumlah: number }[];
  bayar: Rp1PenderitaBayar;
  kelas: Rp1PenderitaKelas;
  hari_rawat: { bayi: number; anak_dewasa: number };
  tt: {
    sensus: number;
    nicu_perinatal: number;
    rawat_gabung: number;
    bayi: number;
    anak_dewasa: number;
  };
  bor: { bayi: number | null; anak_dewasa: number | null };
  days_in_month: number;
  catatan: {
    tanpa_tgl_lahir: number;
    unit_tambahan: string[];
    tt_snapshot: string;
    kelas_tidak_tercatat: number;
  };
}

export interface Rp1JumlahLP {
  L: number | null;
  P: number | null;
  jumlah: number | null;
}

export interface Rp1PenderitaMeninggalBaris {
  dirawat: Rp1JumlahLP;
  lt48: Rp1JumlahLP;
  ge48: Rp1JumlahLP;
  meninggal: number | null;
  dirawat_meninggal: number | null;
}

export interface Rp1PenderitaMeninggalWard extends Rp1PenderitaMeninggalBaris {
  no: string;
  label: string;
  units: string[];
  jenis: Rp1PenderitaJenis;
}

export interface Rp1DataPenderitaMeninggal {
  wards: Rp1PenderitaMeninggalWard[];
  ward_total: Rp1PenderitaMeninggalBaris;
  rekap: { L: number; P: number; lt48: number; ge48: number; jumlah: number };
  catatan: {
    tanpa_tgl_lahir: number;
    unit_tambahan: string[];
  };
}

export type Rp1BorJenis =
  | "grup"
  | "bangsal"
  | "judul"
  | "sub"
  | "tambahan"
  | "jumlah"
  | "total";

export interface Rp1BorBaris {
  no: string;
  label: string;
  units: string[];
  jenis: Rp1BorJenis;
  grup: string;
  L: number | null;
  P: number | null;
  jumlah: number | null;
  tt: number | null;
  ld: number | null;
  hp: number | null;
  bor: number | null;
  los: number | null;
  toi: number | null;
  bto: number | null;
  hari: number | null;
  tt_x_hari: number | null;
  keluar: number | null;
  masuk: number | null;
  awal: number | null;
  akhir: number | null;
}

export interface Rp1DataBor {
  rows: Rp1BorBaris[];
  total: Rp1BorBaris;
  days_in_month: number;
  catatan: {
    tanpa_tgl_lahir: number;
    unit_tambahan: string[];
    tanpa_tt: string[];
    rawat_gabung_tanpa_bayi: boolean;
    tt_snapshot: string;
  };
}

export interface Rp1Filter {
  tahun: number;
  bulan: number;
  unit?: string[];
}

async function openRp1Pdf(report: Rp1Report, f: Rp1Filter): Promise<void> {
  const res = await http
    .getDataParam(`/laporan/rp1-ranap/${report}/pdf`, {
      params: { tahun: f.tahun, bulan: f.bulan, unit: f.unit },
      responseType: "blob"
    })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });

  const url = URL.createObjectURL(
    new Blob([res.data], { type: "application/pdf" })
  );
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function downloadRp1Excel(report: Rp1Report, f: Rp1Filter): Promise<void> {
  const res = await http
    .getDataParam(`/laporan/rp1-ranap/${report}/excel`, {
      params: { tahun: f.tahun, bulan: f.bulan, unit: f.unit },
      responseType: "blob"
    })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });

  const url = URL.createObjectURL(
    new Blob([res.data], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `rp1-${report}_${f.tahun}-${String(f.bulan).padStart(2, "0")}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function rp1Preview(report: Rp1Report, f: Rp1Filter): Promise<any> {
  const res = await http
    .getDataParam(`/laporan/rp1-ranap/${report}`, {
      params: { tahun: f.tahun, bulan: f.bulan, unit: f.unit }
    })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

function downloadExcel(filter: Rl1RanapFilter): void {
  window.open(`${http.getAPI()}/laporan/rl1-ranap/excel?${toQuery(filter)}`);
}

export interface RanapBedCapacityRow {
  id: number;
  unit_kode: string;
  unit_nama: string;
  kelas: string;
  kapasitas: number;
  tgl_efektif: string;
  tgl_nonaktif: string | null;
  is_active: boolean;
  keterangan: string | null;
}

async function listCapacities(params?: {
  unit_kode?: string;
  only_active?: boolean;
}): Promise<{ data: RanapBedCapacityRow[]; kelas_options: string[] }> {
  const res = await http
    .getDataParam(`/referensi/ranap-bed-capacity`, { params })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

async function saveCapacity(
  payload: Partial<RanapBedCapacityRow>,
  id?: number
): Promise<RanapBedCapacityRow> {
  const url = id
    ? `/referensi/ranap-bed-capacity/${id}`
    : `/referensi/ranap-bed-capacity`;
  const res = await (id
    ? http.putData(url, payload)
    : http.postData(url, payload)
  ).catch(err => {
    ErrorAlert.showErrorAlert(err);
    return Promise.reject(err);
  });
  return res.data;
}

async function deleteCapacity(id: number): Promise<void> {
  await http.deleteData(`/referensi/ranap-bed-capacity/${id}`).catch(err => {
    ErrorAlert.showErrorAlert(err);
    return Promise.reject(err);
  });
}

export default {
  preview,
  openPdf,
  downloadExcel,
  openRp1Pdf,
  downloadRp1Excel,
  rp1Preview,
  listCapacities,
  saveCapacity,
  deleteCapacity
};
